import { ApolloLink } from "@apollo/client";
import { Observable } from "@apollo/client/utilities";
import { classifyFenceDisclosures, fenceStore, harvestFenceSignals } from "@shuttlerock/utils";
import { OperationTypeNode } from "graphql";
import { declaresAtLeast, selectsWriteToken } from "./fence-documents";

/**
 * The response half of the read-your-writes fence (the rywf pilot,
 * docs/state-distribution/rywf-fence-pilot/SCOPE.md, "FE BEHAVIOR slice").
 * Convention-driven, zero per-mutation code:
 *
 * - A minting MUTATION's response (its document selects `writeToken` —
 *   fence-documents.ts) is walked for `FencedMutationPayload` mints: any
 *   `writeToken` is captured into the fence store with its payload
 *   associations, including the payload's own convergence disclosure when
 *   the document selects it (the self-fenced signal: the router's
 *   `@requires` associator waited in-response, so the mutation can come
 *   back already converged).
 * - A response to a read that PRESENTED a token (the adapter marked it on
 *   the context) has its convergence disclosures classified and folded
 *   into that token: pending / converged / degraded-to-unfenced. The
 *   fenced document selects exactly ONE disclosure — pinned by the
 *   revision package's comment-fence-contract test, since this fold
 *   aggregates every disclosure the response carries.
 *
 * Every other operation — non-minting mutations, token-less queries,
 * subscriptions — is forwarded UNTOUCHED: no wrapper, no response walks,
 * no per-result context reads. The gate rides the same document-shape
 * predicates as the adapter, so a signal can only ever arrive on an
 * operation the gate admits.
 *
 * This link is the ONLY place the live disclosure is readable: the cache
 * deliberately never stores convergence and serves the synthesized
 * no-claim constant instead (fieldPolicies.ts), so hook data structurally
 * cannot carry it — behavior that needs the pending predicate
 * (`fenced && !converged`) asks the fence store, never response or hook
 * data.
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// The pilot payload family's domain associations, read HERE rather than in
// the platform harvest (the workspace taxonomy's business-type tell:
// `comment`/`revisions` are payload vocabulary, and the walk deliberately
// stops at shape discovery): the entity the token vouches for rides as
// `comment { id }`, the threads the write affected as `revisions { id }`.
// An absent or null half simply yields no association — the store and
// adapter treat that as scope-unknown, never as an error.
const entityIdOf = (payload: Record<string, unknown>): string | undefined => {
  const comment = payload["comment"];
  if (!isRecord(comment)) {
    return undefined;
  }
  const id = comment["id"];
  return typeof id === "string" && id ? id : undefined;
};

const scopeIdsOf = (payload: Record<string, unknown>): string[] => {
  const revisions = payload["revisions"];
  if (!Array.isArray(revisions)) {
    return [];
  }
  return revisions
    .map((revision) => (isRecord(revision) ? revision["id"] : undefined))
    .filter((id): id is string => typeof id === "string" && id.length > 0);
};

export const fenceCaptureLink = new ApolloLink((operation, forward) => {
  const mutationCanMint =
    operation.operationType === OperationTypeNode.MUTATION && selectsWriteToken(operation.query);
  const queryCanPresent =
    operation.operationType === OperationTypeNode.QUERY && declaresAtLeast(operation.query);
  if (!mutationCanMint && !queryCanPresent) {
    return forward(operation);
  }

  const observe = (data: unknown) => {
    if (!data) {
      return;
    }
    if (mutationCanMint) {
      const { captures } = harvestFenceSignals(data);
      captures.forEach(({ token, payload, disclosures }) => {
        fenceStore.capture({
          token,
          entityId: entityIdOf(payload),
          scopeIds: scopeIdsOf(payload),
          outcome: classifyFenceDisclosures(disclosures),
        });
      });
      return;
    }
    const { fencePresentedToken } = operation.getContext();
    if (typeof fencePresentedToken === "string") {
      const { disclosures } = harvestFenceSignals(data);
      fenceStore.recordDisclosure(fencePresentedToken, classifyFenceDisclosures(disclosures));
    }
  };

  return new Observable<ApolloLink.Result>((subscriber) => {
    const subscription = forward(operation).subscribe({
      next: (result) => {
        try {
          observe("data" in result ? result.data : undefined);
        } catch {
          // Fence bookkeeping must never break response delivery; a
          // missed capture only degrades the next read to legacy.
        }
        subscriber.next(result);
      },
      error: (error) => subscriber.error(error),
      complete: () => subscriber.complete(),
    });
    return () => subscription.unsubscribe();
  });
});
